import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useSession } from '@/stores/session';

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/Dashboard.vue') },
  { path: '/requests', name: 'requests', component: () => import('@/views/Requests.vue') },
  { path: '/invoices', name: 'invoices', component: () => import('@/views/Invoices.vue') },
  // Open to every role. Everyone reviews what they submitted; finance and admin
  // see everyone's and can decide. The difference is scope, not access, so the
  // API decides it per-endpoint rather than the route locking the page.
  { path: '/review', name: 'review', component: () => import('@/views/Review.vue') },
  {
    path: '/exports', name: 'exports',
    component: () => import('@/views/Exports.vue'),
    meta: { roles: ['admin', 'finance'] },
  },
  {
    path: '/admin', name: 'admin',
    component: () => import('@/views/Admin.vue'),
    meta: { roles: ['admin'] },
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue') },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

// Client-side guard, for navigation only. It is a convenience, never a control:
// the API enforces the same roles server-side on every request.
router.beforeEach(to => {
  const roles = to.meta.roles as string[] | undefined;
  if (!roles) return true;
  const { me } = useSession();
  return me && roles.includes(me.role) ? true : { name: 'home' };
});

/**
 * Recover from a deploy that happened while the page was open.
 *
 * Route components are lazy chunks with content-hashed names. A deploy replaces
 * them, so a tab loaded before it holds names that no longer exist: the next
 * navigation requests a deleted file, the server answers with the SPA shell,
 * and the browser refuses it as `text/html` where a module was expected. The
 * app appears to have stopped navigating, with the reason visible only in the
 * console.
 *
 * Reloading picks up the new shell and the new chunk names. This is a real
 * navigation the person asked for, so it reloads straight into where they were
 * going rather than dropping them back at the dashboard.
 *
 * The sessionStorage guard keeps a genuinely missing chunk — a broken build, a
 * half-finished upload — from becoming a reload loop. One attempt per
 * destination; if it fails again the error is left to surface rather than
 * pretending a refresh will fix it.
 */
const RELOAD_KEY = 'griffin:chunk-reload';

router.onError((err, to) => {
  const message = err instanceof Error ? err.message : String(err);
  const isStaleChunk = /dynamically imported module|Importing a module script failed|Failed to fetch/i
    .test(message);
  if (!isStaleChunk) return;

  let alreadyTried = false;
  try {
    alreadyTried = sessionStorage.getItem(RELOAD_KEY) === to.fullPath;
    if (!alreadyTried) sessionStorage.setItem(RELOAD_KEY, to.fullPath);
  } catch {
    // Private mode, or storage disabled. Reloading once is still better than
    // a dead navigation; without the guard a broken build reloads repeatedly,
    // which is the rarer case and the more visible one.
  }
  if (alreadyTried) return;

  window.location.assign(to.fullPath);
});

// A navigation that completes proves the chunks match the shell, so the guard
// is cleared and a later deploy gets its own attempt.
router.afterEach(() => {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* nothing to clear */ }
});
